/**
 * The Starter plan picker (the approved billing design, section 2): billing cycle (annual first), what's included,
 * extra storage (None, or 1–4 × 50 GB), what's due today, then "Continue to checkout" opens Stripe Checkout. Owners and
 * admins on web and desktop only (core BillingViewModel `openPicker`). A bottom sheet on phones.
 */
import { Billing, type BillingScreenState, type BillingViewModel } from "@bundle/core";
import type { BillingInterval } from "@bundle/contracts";
import { Button, Divider, Icon, SegmentedControl, Select, Sheet, Stack, Text } from "../../primitives/index.ts";
import { useT, useTr } from "../../i18n/I18nProvider.tsx";
import { useBillingFormat } from "./format.ts";

const STARTER = Billing.PLAN_CATALOG.starter;

export type PlanPickerSheetProps = {
  vm: BillingViewModel;
  state: BillingScreenState;
  /** "Contact us" about Pro. */
  onContact: () => void;
};

export function PlanPickerSheet({ vm, state, onContact }: PlanPickerSheetProps) {
  const t = useT();
  const tr = useTr();
  const currency = state.billing?.currency;
  const f = useBillingFormat(currency);
  const p = state.picker;
  const q = p.quote;
  const annual = p.interval === "year";
  const addonMonthly = Billing.addonMonthlyCents("starter", currency);
  const included = [
    t("billing.picker.inc.members", { n: STARTER.members ?? 0 }),
    t("billing.picker.inc.storage", { gb: (STARTER.storageBytes ?? 0) / Billing.GB }),
    t("billing.picker.inc.domain"),
    t("billing.picker.inc.apps"),
  ];
  return (
    <Sheet open={p.open} onClose={() => vm.closePicker()} title={t("billing.picker.title")} closeLabel={t("common.close")} testID="billing-picker">
      <Stack gap="5">
        <Stack direction="horizontal">
          <SegmentedControl<BillingInterval>
            options={[
              { value: "year", label: t("billing.cycle.annual", { percent: q.savePercent }) },
              { value: "month", label: t("billing.cycle.monthly") },
            ]}
            value={p.interval}
            onChange={(v) => vm.setInterval(v)}
            accessibilityLabel={t("billing.picker.title")}
            testID="billing-picker-cycle"
          />
        </Stack>
        <Stack gap="0.5">
          <Stack direction="horizontal" align="end" gap="1.5">
            <Text variant="title1" weight="bold" testID="billing-picker-price">
              {f.price(q.monthlyCents)}
            </Text>
            <Text color="text.secondary">{t("billing.price.perMonth")}</Text>
          </Stack>
          <Text variant="footnote" color="text.secondary" testID="billing-picker-billed">
            {annual ? t("billing.picker.billedYearly", { amount: f.price(Billing.periodCents("starter", "year", 0, currency)) }) : t("billing.picker.billedMonthly")}
          </Text>
        </Stack>
        <Stack gap="2">
          {included.map((line) => (
            <Stack key={line} direction="horizontal" gap="2.5" align="start">
              <Icon name="check" size="icon.sm" color="status.success" />
              <Text variant="callout">{line}</Text>
            </Stack>
          ))}
        </Stack>
        <Select<string>
          label={t("billing.picker.storage.label")}
          helperText={t("billing.picker.storage.hint", { price: f.price(addonMonthly) })}
          value={String(p.addons)}
          onChange={(v) => vm.setAddons(Number(v))}
          options={Billing.STORAGE_ADDON_CHOICES.map((n) => ({
            value: String(n),
            label: n === 0 ? t("billing.picker.storage.none") : t("billing.picker.storage.option", { gb: n * ((STARTER.addonBytes ?? 0) / Billing.GB) }),
          }))}
          testID="billing-picker-storage"
        />
        <Divider />
        <Stack gap="1">
          <Stack direction="horizontal" justify="between" gap="3">
            <Text color="text.secondary">{t("billing.picker.dueToday")}</Text>
            <Text weight="semibold" testID="billing-picker-due">
              {f.amount(q.dueTodayCents)}
            </Text>
          </Stack>
          {q.firstChargeAt === undefined ? null : (
            <Text variant="caption" color="text.tertiary" testID="billing-picker-trial-kept">
              {t("billing.picker.trialKept", { amount: f.amount(q.periodCents), date: f.date(q.firstChargeAt) })}
            </Text>
          )}
        </Stack>
        <Stack gap="2">
          <Button label={t("billing.continue")} icon="externalLink" variant="primary" size="lg" loading={p.busy} onPress={() => void vm.checkout()} testID="billing-picker-continue" />
          {p.error === undefined ? null : (
            <Text variant="footnote" color="status.danger" align="center" testID="billing-picker-error">
              {tr(p.error)}
            </Text>
          )}
          <Text variant="caption" color="text.tertiary" align="center">
            {t("billing.picker.foot")}
          </Text>
        </Stack>
        <Stack direction="horizontal" justify="center" align="center" gap="1" wrap>
          <Text variant="footnote" color="text.secondary">
            {t("billing.picker.pro", { n: STARTER.members ?? 0 })}
          </Text>
          <Button label={t("billing.contactPro")} variant="ghost" size="sm" onPress={onContact} testID="billing-picker-contact" />
        </Stack>
      </Stack>
    </Sheet>
  );
}
